import { IOrganization } from '@mealime/mealime-api-client'
import { BalanceScaleRight, Book, Bookmark } from '@util/icons'
import * as Pricing from '@util/pricing'
import * as Style from '@util/style'
import {
  ButtonPrimary,
  Logo,
  mediaBreakpointUp,
  useTrackViewAnonymous
} from '@util/ui'
import lodash from 'lodash'
import * as React from 'react'
import plateOne from './offer-1-mobile.png'
import plateTwo from './offer-2-mobile.png'
import { ReactComponent as PrimaryStroke } from './primary-stroke.svg'
import { ReactComponent as SecondaryStroke } from './secondary-stroke.svg'

interface IProps {
  organization: IOrganization
  onCreateAccount: () => void
}

const PerkOffer: React.FC<IProps> = ({ organization, onCreateAccount }) => {
  useTrackViewAnonymous('my_web:viewed_perk_offer_with_organization')
  const paymentOption = 'monthly'

  return (
    <div
      css={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: '100%',
        paddingTop: '2rem',
        paddingBottom: '2rem',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        backgroundColor: Style.COLOR.GREY_100,

        [mediaBreakpointUp('sm')]: {
          backgroundColor: 'transparent'
        }
      }}
    >
      <img
        src={plateOne}
        alt="Yummy dinner"
        css={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '75%',
          [mediaBreakpointUp('md')]: { display: 'none' }
        }}
      />
      <img
        src={plateTwo}
        alt="Yummy breakfast"
        css={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '60%',
          [mediaBreakpointUp('md')]: { display: 'none' }
        }}
      />
      <div
        css={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 448,
          marginTop: '2rem',
          marginBottom: ' auto',
          marginLeft: ' auto',
          marginRight: ' auto',
          zIndex: 1, // raise page content above the dinner plates

          [mediaBreakpointUp('md')]: {
            marginTop: 'auto'
          }
        }}
      >
        <Logo
          height="1.5rem"
          style={{
            textAlign: 'center',
            color: Style.COLOR.GREY_800,
            marginLeft: ' auto',
            marginRight: ' auto',
            marginBottom: '1.5rem',
            paddingRight: '1.5rem' // optical center
          }}
        />
        <div
          style={{
            color: Style.COLOR.GREY_700,
            fontSize: '1.5rem',
            lineHeight: 1.2,
            marginBottom: '2rem'
          }}
        >
          <span style={{ position: 'relative' }}>
            <PrimaryStroke
              style={{
                position: 'absolute',
                top: '-0.15rem',
                left: 2,
                transform: 'rotate(-2deg)'
              }}
            />
            <strong
              style={{
                position: 'relative',
                zIndex: 1,
                color: Style.COLOR.GREY_800
              }}
            >
              Save {Pricing.getDiscountString(organization.coupon)}
            </strong>
          </span>{' '}
          on Mealime Pro {paymentOption} with{' '}
          <strong style={{ color: Style.COLOR.GREY_800 }}>
            {lodash.startCase(organization.name)}
          </strong>
          .
        </div>

        <div
          style={{
            fontSize: '1rem',
            fontWeight: 300,
            textAlign: 'center',
            color: Style.COLOR.GREY_800,
            marginBottom: '1rem'
          }}
        >
          Unlock healthy and stress-free eating!
        </div>
        <div
          css={{
            fontSize: '1.125rem',
            color: Style.COLOR.GREY_900,
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
            backgroundColor: '#fff',
            boxShadow: '0px 8px 12px -12px rgba(0, 0, 0, 0.15)',
            borderRadius: '1.5rem',

            [mediaBreakpointUp('md')]: {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: Style.COLOR.GREY_200
            }
          }}
        >
          <Feature icon={<Book />} label="Exclusive recipes" />
          <Feature
            icon={<BalanceScaleRight />}
            label="Nutritional information"
          />
          <Feature icon={<Bookmark />} label="Collect unlimited recipes" />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '1.5rem'
          }}
        >
          <ButtonPrimary large shadow onClick={onCreateAccount}>
            Create an Account
          </ButtonPrimary>
        </div>
        <div
          style={{
            alignSelf: 'center',
            textAlign: 'center',
            marginTop: '1rem',
            lineHeight: 1.4,
            backgroundColor: 'rgba(248, 248, 248, 0.5)',
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            borderRadius: '0.5rem',
            backdropFilter: 'blur(6px)'
          }}
        >
          <div
            style={{
              fontSize: '0.875rem',
              color: Style.COLOR.GREY_700
            }}
          >
            Get Mealime Pro for
          </div>

          {paymentOption === 'monthly' && (
            <div style={{ fontSize: '1.125rem' }}>
              <span style={{ position: 'relative' }}>
                <SecondaryStroke
                  style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%'
                  }}
                />
                <span
                  style={{
                    position: 'relative',
                    fontWeight: 700,
                    color: Style.COLOR.GREY_700,
                    zIndex: 1
                  }}
                >
                  {Pricing.getDiscountedPriceString(
                    paymentOption,
                    organization.coupon
                  )}
                </span>
                <span
                  style={{
                    position: 'relative',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: Style.COLOR.GREY_700,
                    zIndex: 1
                  }}
                >
                  /month
                </span>
              </span>{' '}
              <span
                style={{
                  color: Style.COLOR.GREY_600,
                  textDecoration: 'line-through'
                }}
              >
                {Pricing.getBasePriceString(paymentOption)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Feature = ({ label, icon }: { label: string; icon: JSX.Element }) => (
  <div
    css={{
      display: 'flex',
      alignItems: 'flex-start',
      fontSize: '1.125rem',
      lineHeight: 1.2,
      paddingTop: '1rem',
      paddingBottom: '1rem',
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',

      '& + &': {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: Style.COLOR.GREY_200
      },

      [mediaBreakpointUp('md')]: {
        paddingLeft: '2rem',
        paddingRight: '2rem'
      }
    }}
  >
    <div style={{ color: Style.COLOR.GREY_600, marginRight: '0.75rem' }}>
      {icon}
    </div>
    <div style={{ flex: 1, marginLeft: '0.5rem' }}>{label}</div>
  </div>
)

export default PerkOffer
